
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface MobileMenuContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  userName: string;
  isAdmin: boolean;
  closeMenu: () => void;
}

const MobileMenuContext = createContext<MobileMenuContextType | undefined>(undefined);

export const useMobileMenu = () => {
  const context = useContext(MobileMenuContext);
  if (!context) {
    throw new Error('useMobileMenu must be used within a MobileMenuProvider');
  }
  return context;
};

interface MobileMenuProviderProps {
  children: React.ReactNode;
  user: any;
}

export const MobileMenuProvider = ({ children, user }: MobileMenuProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.id) {
        try {
          // Fetch user name
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('name')
            .eq('id', user.id)
            .single();
          
          if (!userError && userData) {
            setUserName(userData.name);
          }

          // Check admin role
          const { data: adminData, error: adminError } = await supabase.rpc('has_role', { _role: 'admin' });
          
          if (!adminError) {
            setIsAdmin(!!adminData);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    
    fetchUserData();
  }, [user]);

  const closeMenu = () => setIsOpen(false);

  return (
    <MobileMenuContext.Provider value={{ isOpen, setIsOpen, userName, isAdmin, closeMenu }}>
      {children}
    </MobileMenuContext.Provider>
  );
};
